import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { 
  User, 
  Building2, 
  FileText, 
  Search, 
  MessageSquare, 
  Handshake,
  CheckCircle2,
  CheckCircle,
  Circle,
  ArrowRight,
  Sparkles,
  Target,
  UserCheck,
  Send,
  FileSignature,
  PlayCircle,
  BarChart3,
  Star,
  ChevronRight,
  Rocket,
  Clock,
  Calendar
} from 'lucide-react';
import { useFreelanceStatus } from '../hooks/useFreelanceStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GettingStartedPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [profileCompleteness, setProfileCompleteness] = useState(0);
  const [stats, setStats] = useState({
    resources: 0,
    projects: 0,
    matches: 0,
    messages: 0,
    collaborations: 0
  });

  const isRM = user?.role === 'resource_manager' || user?.role === 'operating_partner';
  const isFreelance = user?.is_freelance === true;
  const token = localStorage.getItem('token');
  
  // Get freelance status for represented freelancers
  const { freelanceStatus } = useFreelanceStatus(false);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      // Fetch profile completeness
      const profileRes = await axios.get(`${API}/users/profile-completeness`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileCompleteness(profileRes.data.completeness || 0);

      // Fetch actual counts from multiple endpoints
      let resourceCount = 0;
      let projectCount = 0;
      let matchCount = 0;
      let messageCount = 0;
      let collaborationCount = 0;
      let completedCollaborationCount = 0;
      let templateCount = 0;
      let reservationCount = 0;

      // Get resources count (for RM) - Use /my endpoint to get only user's resources
      try {
        const resourcesRes = await axios.get(`${API}/resources/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const resources = resourcesRes.data || [];
        resourceCount = Array.isArray(resources) ? resources.length : 0;
      } catch (e) {
        console.log('Could not fetch resources');
      }

      // Get projects count (for PM) - Use /my endpoint to get only user's projects
      try {
        const projectsRes = await axios.get(`${API}/projects/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const projects = projectsRes.data || [];
        projectCount = Array.isArray(projects) ? projects.length : 0;
      } catch (e) {
        console.log('Could not fetch projects');
      }

      // Get collaborations count
      try {
        const collabRes = await axios.get(`${API}/collaborations/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const collabs = collabRes.data?.collaborations || collabRes.data || [];
        collaborationCount = Array.isArray(collabs) ? collabs.length : 0;
        // Count completed collaborations OR those that have been rated
        // Data structure: each item has { collaboration: {...}, has_rated: bool, my_rating: {...} }
        if (Array.isArray(collabs)) {
          completedCollaborationCount = collabs.filter(c => {
            const status = c.collaboration?.status || c.status;
            const hasRated = c.has_rated === true;
            return status === 'completed' || status === 'closed' || hasRated;
          }).length;
        }
      } catch (e) {
        console.log('Could not fetch collaborations', e.message);
      }

      // Get messages count (conversations/threads)
      try {
        const msgRes = await axios.get(`${API}/messages/conversations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const conversations = msgRes.data || [];
        messageCount = Array.isArray(conversations) ? conversations.length : 0;
      } catch (e) {
        console.log('Could not fetch messages', e.message);
      }

      // Get contract templates count - only count USER-created templates, not system predefined ones
      try {
        const templatesRes = await axios.get(`${API}/contract-templates/my-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const templates = templatesRes.data?.templates || templatesRes.data || [];
        // Filter out predefined/system templates - only count user-created ones
        const userTemplates = Array.isArray(templates) 
          ? templates.filter(t => !t.is_predefined && t.is_predefined !== true)
          : [];
        templateCount = userTemplates.length;
      } catch (e) {
        console.log('Could not fetch templates', e.message);
      }

      // Get reservations (pre-bookings) count - both incoming and outgoing
      try {
        const [incomingRes, outgoingRes] = await Promise.all([
          axios.get(`${API}/pre-reservations/incoming`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/pre-reservations/outgoing`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        const incoming = incomingRes.data?.reservations || incomingRes.data || [];
        const outgoing = outgoingRes.data?.reservations || outgoingRes.data || [];
        reservationCount = (Array.isArray(incoming) ? incoming.length : 0) + 
                          (Array.isArray(outgoing) ? outgoing.length : 0);
      } catch (e) {
        console.log('Could not fetch reservations', e.message);
      }

      // "Matches" represents the matching activity done:
      // - Reservations (pre-bookings) = active matching requests
      // - Collaborations come FROM reservations, so we don't double count
      // matchCount = reservations only (the matching requests made/received)
      matchCount = reservationCount;

      setStats({
        resources: resourceCount,
        projects: projectCount,
        matches: matchCount,
        messages: messageCount,
        collaborations: collaborationCount
      });

      // Determine completed steps based on actual data
      const completed = [];
      const userIsRM = user?.role === 'resource_manager' || user?.role === 'operating_partner';
      
      // Step 1: Profile - considered complete at 90% to encourage full completion
      // This ensures users are prompted to fully complete their profile before moving on
      if (profileRes.data.completeness >= 90) {
        completed.push('profile');
      }
      
      // Step 2: Create resource/project - check BOTH counts to be safe
      if (resourceCount > 0 || projectCount > 0) {
        completed.push('create');
      }
      
      // Step 3: Match - if they have collaborations OR used matching feature
      // Having collaborations implies they've done matching
      if (matchCount > 0 || collaborationCount > 0 || reservationCount > 0) {
        completed.push('match');
      }
      
      // Step 4: Contact - if they have messages, reservations (pre-booking), or collaborations
      // Having collaborations implies they've contacted someone
      if (messageCount > 0 || reservationCount > 0 || collaborationCount > 0) {
        completed.push('contact');
      }
      
      // Step 5: Prepare - if they have contract templates
      if (templateCount > 0) {
        completed.push('prepare');
      }
      
      // Step 6: Collaborate - if they have any collaboration
      if (collaborationCount > 0) {
        completed.push('collaborate');
      }
      
      // Step 7: Track - if they have active or ongoing collaborations
      if (collaborationCount > 0) {
        completed.push('track');
      }
      
      // Step 8: Close & evaluate - if they have completed collaborations
      if (completedCollaborationCount > 0) {
        completed.push('close');
      }
      
      console.log('Progress check:', { 
        resourceCount, projectCount, matchCount, messageCount, 
        collaborationCount, completedCollaborationCount, templateCount, reservationCount,
        userIsRM, completedDetails: JSON.stringify(completed) 
      });
      setCompletedSteps(completed);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  // Define steps based on role
  const getSteps = () => {
    const commonSteps = [
      {
        id: 'profile',
        title: 'Complete your profile',
        description: 'Add your personal information and company details to build trust with other users.',
        icon: User,
        color: 'blue',
        action: () => navigate('/profile'),
        actionLabel: 'Edit Profile',
        tips: ['Add a professional photo', 'Include your LinkedIn profile', 'Write a compelling bio']
      }
    ];

    // Check if user is represented freelancer
    const isRepresentedFreelancer = user?.is_freelance && freelanceStatus?.freelance_status === 'represented_free';

    if (isRM) {
      // Resource Manager / Freelance steps
      const rmSteps = [
        ...commonSteps,
        {
          id: 'create',
          title: isFreelance ? 'Create your consultant profile' : 'Create your first resource',
          description: isFreelance 
            ? 'Showcase your ServiceNow expertise and availability to find matching projects.'
            : 'Add consultant profiles to showcase your team\'s ServiceNow expertise.',
          icon: FileText,
          color: 'emerald',
          action: () => navigate('/resources'),
          actionLabel: isFreelance ? 'Create My Profile' : 'Add Resource',
          tips: ['Highlight key certifications', 'Specify availability dates', 'Add relevant experience']
        }
      ];
      
      // For represented freelancers, add adapted steps
      if (isRepresentedFreelancer) {
        return [
          ...rmSteps,
          {
            id: 'wait_match',
            title: 'Wait for project proposals',
            description: 'Your Operating Partner actively promotes your profile. They will send you project opportunities.',
            icon: Clock,
            color: 'purple',
            disabled: true,
            action: null,
            actionLabel: 'Managed by Operating Partner',
            tips: ['Keep your profile up to date', 'Update your availability regularly', 'Respond promptly to proposals']
          },
          {
            id: 'review_proposals',
            title: 'Review & accept reservations',
            description: 'When your Operating Partner finds a match, review the project details and accept the reservation.',
            icon: Calendar,
            color: 'orange',
            action: () => navigate('/reservations'),
            actionLabel: 'View Reservations',
            tips: ['Check project requirements', 'Confirm your availability', 'Communicate with your Partner']
          },
          {
            id: 'collaborate',
            title: 'Start collaborating',
            description: 'Once confirmed, begin working with the client on the project.',
            icon: Handshake,
            color: 'cyan',
            action: () => navigate('/collaborations'),
            actionLabel: 'View Collaborations',
            tips: ['Deliver quality work', 'Track time accurately', 'Communicate proactively']
          },
          {
            id: 'complete',
            title: 'Complete & get paid',
            description: 'Successfully complete the project and receive payment through your Operating Partner.',
            icon: CheckCircle,
            color: 'green',
            action: () => navigate('/collaborations'),
            actionLabel: 'Track Progress',
            tips: ['Submit deliverables on time', 'Get client feedback', 'Build your reputation']
          }
        ];
      }
      
      // Independent freelancer or RM steps
      return [
        ...rmSteps,
        {
          id: 'match',
          title: 'Discover matching projects',
          description: 'Browse projects that match your expertise and reach out to potential clients.',
          icon: Search,
          color: 'purple',
          action: () => navigate('/matching'),
          actionLabel: 'Find Projects',
          tips: ['Use filters to narrow down', 'Check compatibility scores', 'Save interesting projects']
        },
        {
          id: 'contact',
          title: 'Contact project owners',
          description: 'Send messages or pre-booking requests to companies with matching projects.',
          icon: Send,
          color: 'orange',
          action: () => navigate('/matching'),
          actionLabel: 'Start Reaching Out',
          tips: ['Personalize your messages', 'Highlight relevant experience', 'Be responsive']
        },
        {
          id: 'interview',
          title: 'Schedule interviews',
          description: 'Connect your calendar and schedule interviews with interested parties.',
          icon: Calendar,
          color: 'pink',
          action: () => navigate('/profile?tab=integrations'),
          actionLabel: 'Setup Calendar',
          tips: ['Connect Google or Outlook calendar', 'Use video conferencing', 'Prepare interview questions']
        },
        {
          id: 'prepare',
          title: 'Prepare collaboration documents',
          description: 'Set up contract templates to streamline your collaboration process.',
          icon: FileSignature,
          color: 'cyan',
          action: () => navigate('/contract-templates'),
          actionLabel: 'Setup Templates',
          tips: ['Use AI to generate contracts', 'Customize terms', 'Save as reusable templates']
        },
        {
          id: 'collaborate',
          title: 'Launch your first collaboration',
          description: 'Formalize agreements and track your engagements through the platform.',
          icon: Handshake,
          color: 'green',
          action: () => navigate('/collaborations'),
          actionLabel: 'View Collaborations',
          tips: ['Set clear milestones', 'Use built-in tracking', 'Communicate regularly']
        },
        {
          id: 'track',
          title: 'Track & manage collaborations',
          description: 'Monitor ongoing projects, deliverables, and timeline from your dashboard.',
          icon: BarChart3,
          color: 'indigo',
          action: () => navigate('/dashboard'),
          actionLabel: 'Go to Dashboard',
          tips: ['Check progress regularly', 'Update status', 'Document key decisions']
        },
        {
          id: 'close',
          title: 'Close & evaluate',
          description: 'Complete collaborations and leave ratings to build your reputation.',
          icon: Star,
          color: 'yellow',
          action: () => navigate('/collaborations'),
          actionLabel: 'Manage Collaborations',
          tips: ['Request testimonials', 'Provide honest feedback', 'Build long-term relationships']
        }
      ];
    } else {
      // Project Manager steps
      return [
        ...commonSteps,
        {
          id: 'create',
          title: 'Create your first project',
          description: 'Define your ServiceNow resource needs to attract the best consultants.',
          icon: FileText,
          color: 'emerald',
          action: () => navigate('/projects'),
          actionLabel: 'Create Project',
          tips: ['Be specific about requirements', 'Set realistic timelines', 'Define clear deliverables']
        },
        {
          id: 'match',
          title: 'Discover matching consultants',
          description: 'Browse consultant profiles that match your project requirements.',
          icon: Search,
          color: 'purple',
          action: () => navigate('/matching'),
          actionLabel: 'Find Consultants',
          tips: ['Review compatibility scores', 'Check certifications', 'Look at past experience']
        },
        {
          id: 'contact',
          title: 'Contact consultants',
          description: 'Reach out to consultants or send pre-booking requests.',
          icon: MessageSquare,
          color: 'orange',
          action: () => navigate('/matching'),
          actionLabel: 'Start Reaching Out',
          tips: ['Describe your project clearly', 'Ask relevant questions', 'Respond promptly']
        },
        {
          id: 'interview',
          title: 'Schedule interviews',
          description: 'Connect your calendar and conduct interviews to find the perfect match.',
          icon: Calendar,
          color: 'pink',
          action: () => navigate('/profile?tab=integrations'),
          actionLabel: 'Setup Calendar',
          tips: ['Connect Google or Outlook calendar', 'Prepare assessment criteria', 'Use structured interviews']
        },
        {
          id: 'prepare',
          title: 'Prepare collaboration documents',
          description: 'Set up contract templates to streamline your hiring process.',
          icon: FileSignature,
          color: 'cyan',
          action: () => navigate('/contract-templates'),
          actionLabel: 'Setup Templates',
          tips: ['Define standard terms', 'Include NDA clauses', 'Specify payment terms']
        },
        {
          id: 'collaborate',
          title: 'Launch your first collaboration',
          description: 'Formalize agreements and onboard your chosen consultant.',
          icon: Handshake,
          color: 'green',
          action: () => navigate('/collaborations'),
          actionLabel: 'View Collaborations',
          tips: ['Set expectations early', 'Define communication channels', 'Schedule regular check-ins']
        },
        {
          id: 'track',
          title: 'Track & manage collaborations',
          description: 'Monitor progress, deliverables, and timeline from your dashboard.',
          icon: BarChart3,
          color: 'indigo',
          action: () => navigate('/dashboard'),
          actionLabel: 'Go to Dashboard',
          tips: ['Review progress reports', 'Provide timely feedback', 'Address blockers quickly']
        },
        {
          id: 'close',
          title: 'Close & evaluate',
          description: 'Complete collaborations and rate your experience.',
          icon: Star,
          color: 'yellow',
          action: () => navigate('/collaborations'),
          actionLabel: 'Manage Collaborations',
          tips: ['Document lessons learned', 'Provide constructive feedback', 'Consider repeat engagements']
        }
      ];
    }
  };

  const steps = getSteps();
  const completedCount = completedSteps.length;
  const totalSteps = steps.length;
  const progressPercent = Math.round((completedCount / totalSteps) * 100);

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 border-blue-200',
    emerald: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    purple: 'bg-purple-100 text-purple-600 border-purple-200',
    orange: 'bg-orange-100 text-orange-600 border-orange-200',
    cyan: 'bg-cyan-100 text-cyan-600 border-cyan-200',
    green: 'bg-green-100 text-green-600 border-green-200',
    indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200',
    yellow: 'bg-yellow-100 text-yellow-600 border-yellow-200'
  };

  return (
    <div className="p-6 pb-20 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
            <Rocket className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Getting Started</h1>
            <p className="text-gray-500">
              {isFreelance 
                ? 'Your guide to finding ServiceNow projects as a freelance consultant'
                : isRM 
                  ? 'Your guide to placing consultants and finding projects'
                  : 'Your guide to finding the perfect ServiceNow consultants'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-gray-900">Your Progress</h2>
            <p className="text-sm text-gray-500">{completedCount} of {totalSteps} steps completed</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-emerald-600">{progressPercent}%</span>
          </div>
        </div>
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        
        {/* Quick stats */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 mt-6 pt-6 border-t">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{profileCompleteness}%</p>
            <p className="text-xs text-gray-500">Profile Complete</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{isRM ? stats.resources : stats.projects}</p>
            <p className="text-xs text-gray-500">{isRM ? 'Resources' : 'Projects'}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.matches}</p>
            <p className="text-xs text-gray-500">Matches</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.messages}</p>
            <p className="text-xs text-gray-500">Messages</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.collaborations}</p>
            <p className="text-xs text-gray-500">Collaborations</p>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = completedSteps.includes(step.id);
          const isNext = !isCompleted && completedSteps.length === index;
          
          return (
            <div 
              key={step.id}
              className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${
                isNext ? 'ring-2 ring-emerald-500 ring-offset-2' : ''
              } ${isCompleted ? 'opacity-75' : ''}`}
            >
              <div className="p-5">
                <div className="flex items-start gap-4">
                  {/* Step number / check */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted 
                      ? 'bg-emerald-100 text-emerald-600' 
                      : isNext 
                        ? `${colorClasses[step.color]}` 
                        : 'bg-gray-100 text-gray-400'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <span className="font-semibold">{index + 1}</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className={`font-semibold ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                          {step.title}
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">{step.description}</p>
                      </div>
                      
                      {!isCompleted && (
                        <Button
                          size="sm"
                          onClick={step.action}
                          className={isNext 
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }
                        >
                          {step.actionLabel}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>

                    {/* Tips - show for next step */}
                    {isNext && step.tips && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          <Sparkles className="w-3 h-3 inline mr-1" />
                          Tips for this step
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {step.tips.map((tip, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-center gap-1.5">
                              <div className="w-1 h-1 rounded-full bg-emerald-500" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Help section */}
      <div className="mt-8 mb-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-lg mb-1 text-white">Need help?</h3>
            <p className="text-white/70 text-sm mb-4">
              Our support team is here to help you get the most out of Snow Exchange.
            </p>
            <Button 
              variant="outline" 
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              onClick={() => navigate('/support')}
            >
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GettingStartedPage;
